import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  FlaskConical,
  Search,
  Plus,
  Trash2,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle2,
  FileText,
  User
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResultEntryPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking');
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [icd10Search, setIcd10Search] = useState('');
  const [icd10Results, setIcd10Results] = useState([]);
  const [selectedIcd10Codes, setSelectedIcd10Codes] = useState([]);
  
  const [resultData, setResultData] = useState({
    parameters: [],
    interpretation: '',
    doctor_notes: ''
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails(bookingId);
    }
  }, [bookingId]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/bookings?status=confirmed`),
        axios.get(`${API}/lab-templates`)
      ]);
      setBookings(bookingsRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookingDetails = async (id) => {
    try {
      const response = await axios.get(`${API}/bookings/${id}`);
      setSelectedBooking(response.data);
    } catch (error) {
      console.error('Error fetching booking:', error);
    }
  };

  const searchIcd10 = async (query) => {
    if (query.length < 2) {
      setIcd10Results([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/icd10/search?q=${encodeURIComponent(query)}&limit=10`);
      setIcd10Results(response.data);
    } catch (error) {
      console.error('Error searching ICD-10:', error);
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      setResultData(prev => ({
        ...prev,
        parameters: template.parameters.map(p => ({
          name: p.name,
          value: '',
          unit: p.unit,
          reference_range: p.reference_range,
          normal_range_min: p.normal_range_min,
          normal_range_max: p.normal_range_max,
          status: 'pending'
        }))
      }));
    }
  };

  const handleParameterChange = (index, value) => {
    setResultData(prev => {
      const newParams = [...prev.parameters];
      const param = newParams[index];
      param.value = value;
      
      // Determine if value is within normal range
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && param.normal_range_min !== undefined && param.normal_range_max !== undefined) {
        if (numValue < param.normal_range_min) {
          param.status = 'low';
        } else if (numValue > param.normal_range_max) {
          param.status = 'high';
        } else {
          param.status = 'normal';
        }
      } else if (value) {
        param.status = 'normal';
      }
      
      return { ...prev, parameters: newParams };
    });
  };

  const addIcd10Code = (code) => {
    if (!selectedIcd10Codes.find(c => c.code === code.code)) {
      setSelectedIcd10Codes(prev => [...prev, code]);
    }
    setIcd10Search('');
    setIcd10Results([]);
  };

  const removeIcd10Code = (code) => {
    setSelectedIcd10Codes(prev => prev.filter(c => c.code !== code));
  };

  const handleSubmit = async () => {
    if (!selectedBooking) {
      toast.error('Please select a booking');
      return;
    }
    
    if (!selectedTemplate) {
      toast.error('Please select a test template');
      return;
    }
    
    const hasValues = resultData.parameters.some(p => p.value);
    if (!hasValues) {
      toast.error('Please enter at least one parameter value');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        booking_id: selectedBooking.id,
        patient_id: selectedBooking.patient_id,
        test_id: selectedBooking.test_ids[0] || '',
        test_name: selectedTemplate.test_name,
        test_type: selectedTemplate.test_type || '',
        result_type: selectedTemplate.test_type || selectedTemplate.result_type || '',
        parameters: resultData.parameters.filter(p => p.value),
        interpretation: resultData.interpretation,
        doctor_notes: resultData.doctor_notes,
        icd10_codes: selectedIcd10Codes.map(c => c.code)
      };

      await axios.post(`${API}/results/manual`, payload);
      toast.success('Lab result saved successfully');
      navigate('/results');
    } catch (error) {
      console.error('Error saving result:', error);
      toast.error(error.response?.data?.detail || 'Failed to save result');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      low: 'bg-blue-50 text-blue-700',
      normal: 'bg-emerald-50 text-emerald-700',
      high: 'bg-red-50 text-red-700',
      pending: 'bg-slate-50 text-slate-500'
    };
    return <Badge className={styles[status] || styles.pending}>{status}</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="result-entry-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Enter Lab Results</h1>
            <p className="text-muted-foreground mt-1">Manually input test results for patient bookings</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
          </div>
        ) : (
          <>
            {/* Step 1: Select Booking */}
            <div className="card-clinical">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-sky-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                Select Booking
              </h2>
              
              <Select 
                value={selectedBooking?.id || ''} 
                onValueChange={(id) => {
                  const booking = bookings.find(b => b.id === id);
                  setSelectedBooking(booking);
                }}
              >
                <SelectTrigger data-testid="booking-select">
                  <SelectValue placeholder="Select a confirmed booking" />
                </SelectTrigger>
                <SelectContent>
                  {bookings.map((booking) => (
                    <SelectItem key={booking.id} value={booking.id}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">#{booking.id.slice(0, 8)}</span>
                        <span className="text-muted-foreground">- {booking.appointment_date}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedBooking && (
                <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Booking ID:</span>
                      <span className="ml-2 font-medium">#{selectedBooking.id.slice(0, 8)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Date:</span>
                      <span className="ml-2 font-medium">{selectedBooking.appointment_date}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Patient ID:</span>
                      <span className="ml-2 font-medium">{selectedBooking.patient_id?.slice(0, 8)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Status:</span>
                      <Badge className="ml-2 bg-emerald-50 text-emerald-700">{selectedBooking.status}</Badge>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Step 2: Select Test Template */}
            <div className="card-clinical">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-sky-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                Select Test Template
              </h2>
              
              <Select value={selectedTemplate?.id || ''} onValueChange={handleTemplateSelect}>
                <SelectTrigger data-testid="template-select">
                  <SelectValue placeholder="Select a test template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      <div className="flex items-center gap-2">
                        <FlaskConical className="w-4 h-4" />
                        <span>{template.test_name}</span>
                        <Badge variant="outline" className="text-xs">{template.category}</Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Step 3: Enter Parameters */}
            {selectedTemplate && (
              <div className="card-clinical">
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 bg-sky-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                  Enter Test Parameters
                </h2>
                
                <div className="space-y-4">
                  <div className="grid gap-4">
                    {/* Header */}
                    <div className="grid grid-cols-8 gap-2 text-sm font-medium text-muted-foreground px-2">
                      <div className="col-span-4">Parameter</div>
                      <div className="col-span-4">Result</div>
                    </div>
                    
                    {/* Parameters */}
                    {resultData.parameters.map((param, index) => (
                      <div key={index} className="grid grid-cols-8 gap-2 items-center p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <div className="col-span-4">
                          <span className="font-medium text-sm">{param.name}</span>
                        </div>
                        <div className="col-span-4">
                          <Input
                            type="text"
                            value={param.value}
                            onChange={(e) => handleParameterChange(index, e.target.value)}
                            placeholder="Enter result"
                            className="h-8"
                            data-testid={`param-value-${index}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: ICD-10 Codes */}
            <div className="card-clinical">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-sky-600 text-white rounded-full flex items-center justify-center text-sm">4</span>
                ICD-10 Diagnosis Codes (Optional)
              </h2>
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={icd10Search}
                  onChange={(e) => {
                    setIcd10Search(e.target.value);
                    searchIcd10(e.target.value);
                  }}
                  placeholder="Search ICD-10 codes (e.g., diabetes, hypertension)"
                  className="pl-10"
                  data-testid="icd10-search"
                />
              </div>

              {/* Search Results */}
              {icd10Results.length > 0 && (
                <div className="mt-2 border rounded-lg divide-y max-h-48 overflow-y-auto">
                  {icd10Results.map((code) => (
                    <button
                      key={code.code}
                      onClick={() => addIcd10Code(code)}
                      className="w-full p-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    >
                      <span className="font-mono font-medium text-sky-600">{code.code}</span>
                      <span className="ml-2 text-sm">{code.description}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Selected Codes */}
              {selectedIcd10Codes.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {selectedIcd10Codes.map((code) => (
                    <Badge key={code.code} variant="outline" className="flex items-center gap-1 py-1">
                      <span className="font-mono">{code.code}</span>
                      <span className="text-muted-foreground">- {code.description}</span>
                      <button onClick={() => removeIcd10Code(code.code)} className="ml-1 hover:text-red-500">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Step 5: Interpretation & Notes */}
            <div className="card-clinical">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-sky-600 text-white rounded-full flex items-center justify-center text-sm">5</span>
                Interpretation & Notes
              </h2>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="interpretation">Result Interpretation</Label>
                  <Textarea
                    id="interpretation"
                    value={resultData.interpretation}
                    onChange={(e) => setResultData(prev => ({ ...prev, interpretation: e.target.value }))}
                    placeholder="Enter clinical interpretation of the results..."
                    className="mt-1"
                    rows={3}
                    data-testid="interpretation-input"
                  />
                </div>
                <div>
                  <Label htmlFor="notes">Doctor's Notes</Label>
                  <Textarea
                    id="notes"
                    value={resultData.doctor_notes}
                    onChange={(e) => setResultData(prev => ({ ...prev, doctor_notes: e.target.value }))}
                    placeholder="Additional notes or recommendations..."
                    className="mt-1"
                    rows={3}
                    data-testid="notes-input"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={submitting || !selectedBooking || !selectedTemplate}
                className="bg-sky-600 hover:bg-sky-700"
                data-testid="submit-result-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Result
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ResultEntryPage;
